/**
 * Classifies media files into in-browser preview kinds for the shared
 * MediaPreviewModal. Anything unrecognised falls back to NONE, where the
 * UI offers a download instead.
 *
 * Video is limited to formats with broad mobile-browser support (MOV is
 * excluded — Safari-only). DOC/DOCX deliberately map to NONE: desktop
 * previews them via the external Google Docs Viewer, which would send
 * presigned internal URLs to a third party.
 */
export const MediaPreviewKind = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  PDF: 'PDF',
  NONE: 'NONE',
} as const;

export type MediaPreviewKind = (typeof MediaPreviewKind)[keyof typeof MediaPreviewKind];

const KIND_BY_EXTENSION = new Map<string, MediaPreviewKind>([
  ['png', MediaPreviewKind.IMAGE],
  ['jpg', MediaPreviewKind.IMAGE],
  ['jpeg', MediaPreviewKind.IMAGE],
  ['gif', MediaPreviewKind.IMAGE],
  ['webp', MediaPreviewKind.IMAGE],
  ['mp4', MediaPreviewKind.VIDEO],
  ['webm', MediaPreviewKind.VIDEO],
  ['pdf', MediaPreviewKind.PDF],
]);

/**
 * Returns the preview kind for a file name or bare extension
 * (case-insensitive; accepts "photo.PNG", "pdf", or ".pdf").
 */
export function getMediaPreviewKind(fileNameOrExtension: string): MediaPreviewKind {
  const extension = fileNameOrExtension.split('.').pop()?.trim().toLowerCase() ?? '';
  return KIND_BY_EXTENSION.get(extension) ?? MediaPreviewKind.NONE;
}
